import { useState, useEffect } from 'react';
import { getSignedStorageUrl } from '@/lib/storage-utils';

interface SignedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  bucket: string;
  pathOrUrl: string | null | undefined;
  expiresIn?: number;
  fallback?: React.ReactNode;
}

/**
 * Image component that auto-resolves storage paths to signed URLs.
 * Handles both legacy full URLs and raw file paths.
 */
export function SignedImage({ bucket, pathOrUrl, expiresIn = 3600, fallback, ...imgProps }: SignedImageProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!pathOrUrl) {
      setSignedUrl(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getSignedStorageUrl(bucket, pathOrUrl, expiresIn).then((url) => {
      if (!cancelled) {
        setSignedUrl(url);
        setIsLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [bucket, pathOrUrl, expiresIn]);

  if (!pathOrUrl || (!signedUrl && !isLoading)) {
    return fallback ? <>{fallback}</> : null;
  }

  if (isLoading || !signedUrl) {
    return fallback ? <>{fallback}</> : null;
  }

  return <img {...imgProps} src={signedUrl} />;
}
